import {createAction, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {AxiosError} from "axios";
import {Configuration, GlobalPatientApi, PatientResponseDto, PatientResponseDtoPagedList} from "../api/client-axios";
import {ApiError} from "../model/apiError";
import {Problem} from "../model/problem";
import {apiConfiguration} from "./server";
import { applyRejection } from "./thunkError";


export interface PatientsNoOrgState {
    patients?: PatientResponseDto[],
    selectedPatient?: PatientResponseDto,
    searchTerm?: string,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "deleting";
    error?: string | Problem;
    code?: number;
}


const initialPatientState: PatientsNoOrgState = {
    status: "idle",
    error: ""
}
export const fetchNoOrgPatients = createAsyncThunk<PatientResponseDtoPagedList, {
    pageNumber?: number,
    pageSize?: number,
    q?: string
}, { rejectValue: ApiError }>('patientsNoOrg/fetchPatients', async (args, thunkApi) => {
    try {

        const api = new GlobalPatientApi(apiConfiguration());
        const response = await api.apiV1AdministrationNoOrganizationPatientsGet(args.pageNumber, args.pageSize, args.q);
        return response.data;

    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({message: error?.response?.data, statusCode: error?.response?.status});
    }
});


export const deleteNoOrgPatient = createAsyncThunk<PatientResponseDtoPagedList, {
    patientId: string
}, { rejectValue: ApiError }>('patientsNoOrg/deletePatient', async (args, thunkApi) => {
    try {

        const api = new GlobalPatientApi(apiConfiguration());
        await api.apiV1AdministrationNoOrgPatientsIdDelete(args.patientId);
        const response = await api.apiV1AdministrationNoOrganizationPatientsGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({message: error?.response?.data, statusCode: error?.response?.status});
    }
});

export const selectNoOrgPatient = createAction("patientsNoOrg/selectPatient", (patient?: PatientResponseDto) => ({payload: patient}));
export const clearNoOrgErrorPatients = createAction("patientsNoOrg/clearErrorPatients");
export const startDeleteNoOrgPatient = createAction("patientsNoOrg/startDeletePatient");
export const updateNoOrgSearchTerm = createAction("patientsNoOrg/searchTerm", (searchTerm: string) => ({payload: searchTerm}));


const patientsNoOrgSlice = createSlice({
    name: 'patientsNoOrg',
    initialState: initialPatientState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchNoOrgPatients.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(fetchNoOrgPatients.fulfilled, (state, action) => {
                state.patients = action.payload.items;
                state.status = "success";
            })
            .addCase(fetchNoOrgPatients.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(deleteNoOrgPatient.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(deleteNoOrgPatient.fulfilled, (state, action) => {
                state.status = "success";
            })
            .addCase(deleteNoOrgPatient.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(selectNoOrgPatient, (state, action) => {
                state.selectedPatient = action.payload;
            })
            .addCase(clearNoOrgErrorPatients, (state) => {
                state.status = "success";
            })
            .addCase(startDeleteNoOrgPatient, (state) => {
                state.status = "deleting";
            })
            .addCase(updateNoOrgSearchTerm, (state, action) => {
                state.searchTerm = action.payload;
            })
        ;


    }
});

export default patientsNoOrgSlice.reducer;
